import {
  ApptTelemedTab,
  DispositionType,
  PractitionerQualificationCode,
  RoleType,
  TelemedAppointmentVisitTabs,
} from 'utils';

export const dataTestIds = {
  closeIcon: 'CloseIcon',
  loadingSpinner: 'loading-spinner',
  loadingScreen: 'loading-screen',
  deleteOutlinedIcon: 'DeleteOutlinedIcon',
  header: {
    userName: 'header-user-name',
  },
  inPersonHeader: {
    container: 'in-person-header-container',
    patientName: 'patient-name',
    appointmentStatus: 'appointment-status',
    switchModeButton: (status: string) => `switch-status-to-${status}`,
    intakePractitionerInput: 'intake-practitioner-input',
    providerPractitionerInput: 'provider-practitioner-input',
    changeStatusDropdown: 'change-status-dropdown-in-header',
    allergies: 'header-allergies',
  },
  dashboard: {
    appointmentsTable: (tab: 'prebooked' | 'in-office' | 'completed' | 'cancelled') => `appointments-table-${tab}`,
    addPatientButton: 'add-patient-button',
    approveButton: 'approve-button',
    intakeButton: `intake-button`,
    visitDetailsButton: `visit-details-button`,
    progressNoteButton: `progress-note-button`,
    dischargeButton: 'discharge-button',
    prebookedTab: 'prebooked-tab',
    locationSelect: 'location-select',
    datePickerTodayButton: 'date-picker-today-button',
    loadingIndicator: 'loading-indicator',
    tableRowWrapper: (appointmentId: string) => `appointments-table-row-${appointmentId}`,
    tableRowStatus: (appointmentId: string) => `appointments-table-row-status-${appointmentId}`,
    inOfficeTab: 'in-office-tab',
    groupSelect: 'group-select',
    dischargedTab: 'discharged-tab',
    cancelledTab: 'cancelled-tab',
    arrivedButton: 'arrived-button',
    appointmentStatus: 'appointment-status',
    chatButton: 'Chat-outlined-icon',
    patientName: 'patient-name',
  },
  appointmentPage: {
    patientFullName: 'patient-full-name',
    changeStatusDropdown: 'change-status-dropdown-in-appointment-page',
  },
  addPatientPage: {
    locationHeader: 'location-header',
    mobilePhoneInput: 'mobile-phone-input',
    searchForPatientsButton: 'search-for-patients-button',
    addButton: 'add-button',
    cancelButton: 'cancel-button',
    patientNotFoundButton: 'patient-not-found-button',
    firstNameInput: 'first-name-input',
    lastNameInput: 'last-name-input',
    sexAtBirthDropdown: 'sex-at-birth-dropdown',
    reasonForVisitDropdown: 'reason-for-visit-dropdown',
    visitTypeDropdown: 'visit-type-dropdown',
    dateFormatValidationError: 'date-format-validation-error',
    prefillForButton: 'prefill-for-button',
    prefilledPatientName: 'prefilled-patient-name',
    prefilledPatientBirthday: 'prefilled-patient-birthday',
    prefilledPatientBirthSex: 'prefilled-patient-birth-sex',
    prefilledPatientEmail: 'prefilled-patient-email',
  },
  pagination: {
    paginationContainer: 'pagination-container',
  },
  patientInformationPage: {
    saveChangesButton: 'save-changes-button',
    addInsuranceButton: 'add-insurance-button',
    breadcrumb: 'breadcrumb',
  },
  patientHeader: {
    patientId: 'header-patient-id',
    patientName: 'header-patient-name',
    patientBirthSex: 'header-patient-birth-sex',
    patientBirthday: 'header-patient-birthday',
    patientAddress: 'header-patient-address',
    patientPhoneNumber: 'header-patient-phone-number',
    emergencyContact: 'header-patient-emergency-contact',
    closeButton: 'header-patient-close-button',
  },
  patientInformationContainer: {
    patientLastName: 'patient-last-name',
    patientFirstName: 'patient-first-name',
    patientBirthSex: 'patient-birth-sex',
    patientDateOfBirth: 'patient-date-of-birth',
    patientMiddleName: 'patient-middle-name',
    patientSuffix: 'patient-suffix',
    patientPreferredName: 'patient-preferred-name',
    patientPreferredPronouns: 'patient-preferred-pronouns',
  },
  contactInformationContainer: {
    streetAddress: 'street-address',
    addressLineOptional: 'address-line-optional',
    city: 'city',
    state: 'state',
    zip: 'zip',
    patientMobile: 'patient-mobile',
    patientEmail: 'patient-email',
  },
  patientDetailsContainer: {
    patientsEthnicity: 'patients-ethnicity',
    patientsRace: 'patients-race',
    sexualOrientation: 'sexual-orientation',
    genderIdentity: 'gender-identity',
    howDidYouHearAboutUs: 'how-did-you-hear-about-us',
    sendMarketingMessages: 'send-marketing-messages',
    preferredLanguage: 'preferred-language',
    commonWellConsent: 'common-well-consent',
    pleaseSpecifyField: 'please-specify-field',
  },
  primaryCarePhysicianContainer: {
    pcpCheckbox: 'pcp-checkbox',
    firstName: 'primaryCarePhysicianContainer-first-name',
    lastName: 'primaryCarePhysicianContainer-last-name',
    practiceName: 'primaryCarePhysicianContainer-practice-name',
    address: 'primaryCarePhysicianContainer-address',
    mobile: 'primaryCarePhysicianContainer-mobile',
  },
  insuranceContainer: {
    type: 'insurance-type',
    insuranceCarrier: 'insurance-carrier',
    insurancePlanType: 'insurance-plan-type',
    memberId: 'insurance-member-id',
    showMoreButton: 'insurance-show-more-button',
    hideButton: 'insurance-hide-button',
    policyHoldersFirstName: 'insurance-policy-holders-first-name',
    policyHoldersLastName: 'insurance-policy-holders-last-name',
    policyHoldersMiddleName: 'insurance-policy-holders-middle-name',
    policyHoldersDateOfBirth: 'insurance-policy-holders-date-of-birth',
    policyHoldersSex: 'insurance-policy-holders-sex',
    policyHolderAddressCheckbox: 'insurance-policy-holder-address-checkbox',
    streetAddress: 'insurance-street-address',
    addressLine2: 'insurance-address-line2',
    city: 'insurance-city',
    state: 'insurance-state',
    zip: 'insurance-zip',
    relationship: 'insurance-relationship',
    additionalInformation: 'insurance-additional-information',
    removeButton: 'insurance-remove-button',
  },
  responsiblePartyInformationContainer: {
    id: 'responsible-party-information-container',
    relationshipDropdown: 'responsible-party-information-container-relationship-dropdown',
    firstName: 'responsible-party-information-container-first-name',
    lastName: 'responsible-party-information-container-last-name',
    dateOfBirthDropdown: 'responsible-party-information-container-date-of-birth-dropdown',
    birthSexDropdown: 'responsible-party-information-container-birth-sex-dropdown',
    phoneInput: 'responsible-party-information-container-phone-input',
    emailInput: 'responsible-party-information-container-email-input',
    addressLine1: 'responsible-party-information-container-address',
    addressLine2: 'responsible-party-information-container-address-2',
    city: 'responsible-party-information-container-city',
    state: 'responsible-party-information-container-state',
    zip: 'responsible-party-information-container-zip',
  },

  userSettingsContainer: {
    releaseOfInfoDropdown: 'release-of-info-dropdown',
    RxHistoryConsentDropdown: 'Rx-history-consent-dropdown',
  },

  slots: {
    slot: 'slot',
  },
  dialog: {
    closeButton: 'dialog-close-button',
    cancelButton: 'dialog-cancel-button',
    proceedButton: 'dialog-proceed-button',
    title: 'dialog-title',
    message: 'dialog-message',
  },
  virtualLocationsPage: {
    locationsSearch: 'locations-search',
    locationValue: 'location-value',
    operateInLocationValue: 'operate-in-location-value',
    locationRow: (locationId: string) => `location-row-${locationId}`,
  },
  editVirtualLocation: {
    saveChangesButton: 'save-changes-button',
    cancelButton: 'cancel-button',
    operateInLocationToggle: 'operate-in-location-toggle',
    locationNameTitle: 'location-name-title',
    locationNameField: 'location-name-field',
    locationStateField: 'location-state-field',
  },
  patients: {
    searchByLastNameField: 'search-last-name-field',
    searchByGivenNamesField: 'search-given-names-field',
    searchByDateOfBirthField: 'searchByDateOfBirthField',
    searchByPhoneField: 'search-phone-field',
    searchByAddressField: 'search-by-address',
    searchByEmailField: 'search-by-email-field',
    searchByStatusName: 'search-by-status-name',
    searchByLocationName: 'search-by-location-name',
    searchButton: 'search-button',
    resetFiltersButton: 'reset-filters-button',
    patientId: 'patient-id',
    patientName: 'patient-name',
    patientDateOfBirth: 'patient-date-of-birth',
    patientEmail: 'patient-email',
    patientPhoneNumber: 'patient-phone-number',
    patientAddress: 'patient-address',
    searchResultsRowPrefix: 'search-result-row-',
    searchResultRow: (patientId: string) => `${dataTestIds.patients.searchResultsRowPrefix}${patientId}`,
  },
  employeesPage: {
    table: 'employees-providers-content-table',
    providersTabButton: 'providers-tab-button',
    searchByName: 'search-by-name-field',
    providersStateFilter: 'providers-state-filter',
    informationForm: 'employee-information-form',
    firstName: 'employee-first-name',
    middleName: 'employee-middle-name',
    lastName: 'employee-last-name',
    birthDate: 'employee-birth-date',
    email: 'employee-email',
    phone: 'employee-phone',
    fax: 'employee-fax',
    addressLine1: 'employee-address-line-1',
    addressLine2: 'employee-address-line-2',
    addressCity: 'employee-address-city',
    addressState: 'employee-address-state',
    addressZip: 'employee-address-zip',
    rolesSection: 'employee-roles-section',
    roleRow: (employeeRole: RoleType): string => `employee-${employeeRole}-role`,
    providerDetailsProviderTypeDropdown: 'employees-provider-details-provider-type-dropdown',
    providerDetailsProviderTypeOtherText: 'employees-provider-details-provider-type',
    providerDetailsNPI: 'employees-provider-details-npi',
    submitButton: 'employees-form-submit-button',
    qualificationsTable: 'employee-qualifications-table',
    qualificationRow: (code: PractitionerQualificationCode): string => `employee-${code}-qualification`,
    deleteQualificationButton: 'employee-delete-qualification-button',
    addQualificationCard: 'add-new-qualification-card',
    newQualificationStateDropdown: 'new-qualification-state-dropdown',
    newQualificationTypeDropdown: 'new-qualification-type-dropdown',
    newQualificationNumberField: 'new-qualification-number-field',
    newQualificationExpDatePicker: 'new-qualification-expiration-date-picker',
    addQualificationButton: 'add-qualification-button',
    deactivateUserButton: 'deactivate-user-button',
    statusChip: 'employee-status-chip',
  },
  telemedEhrFlow: {
    trackingBoardLocationsSelect: 'telemed-tracking-board-location-select',
    trackingBoardLocationsSelectOption: (id: string) => `telemed-tracking-board-location-select-option-${id}`,
    telemedAppointmentsTabs: (tab: ApptTelemedTab) => `telemed-appointments-tabs-${tab}`,
    trackingBoardTableGroupRow: `telemed-tracking-board-table-group-row`,
    trackingBoardTableRow: (appointmentId: string) => `telemed-tracking-board-table-row-${appointmentId}`,
    myPatientsButton: 'telemed-my-patients-button',
    allPatientsButton: 'telemed-all-patients-button',
    trackingBoardTable: 'telemed-tracking-board-table',
    trackingBoardAssignButton: 'telemed-tracking-board-assign-appointment-button',
    trackingBoardViewButton: (appointmentId?: string) =>
      `telemed-tracking-board-view-appointment-button-${appointmentId}`,
    trackingBoardChatButton: (appointmentId?: string) =>
      `telemed-tracking-board-chat-appointment-button-${appointmentId}`,
    appointmentStatusChip: 'telemed-appointment-status-chip',
    footerButtonConnectToPatient: 'telemed-appointment-footer-button-connect-to-patient',
    footerButtonAssignMe: 'telemed-appointment-footer-button-assign-me',
    footerButtonUnassign: 'telemed-appointment-footer-button-unassign',
    appointmentChartFooter: 'telemed-chart-appointment-footer',
    hpiFieldListLoadingSkeleton: 'telemed-hpi-field-list-loading-skeleton',
    hpiCurrentMedicationsPatientProvidedList: 'telemed-hpi-current-medications-patient-provided-list',
    hpiCurrentMedicationsInput: 'telemed-hpi-current-medications-input',
    hpiCurrentMedicationsDoseInput: 'telemed-hpi-current-medications-dose-input',
    hpiCurrentMedicationsDateTimeInput: 'telemed-hpi-current-medications-date-time-input',
    hpiCurrentMedicationsAddButton: 'telemed-hpi-current-medications-add-button',
    hpiCurrentMedicationsScheduledList: 'telemed-hpi-current-medications-scheduled-list',
    hpiCurrentMedicationsAsNeededList: 'telemed-hpi-current-medications-as-needed-list',
    hpiCurrentMedicationsScheduledRadioButton: 'telemed-hpi-current-medications-scheduled-radio-button',
    hpiCurrentMedicationsAsNeededRadioButton: 'telemed-hpi-current-medications-as-needed-radio-button',
    hpiCurrentMedicationsList: (listType: 'scheduled' | 'as-needed') =>
      `telemed-hpi-current-medications-list-${listType}`,
    hpiCurrentMedicationsListItem: (listDataTestId: string) => `${listDataTestId}-item`,
    hpiCurrentMedicationsColumn: 'telemed-hpi-current-medications-column',
    hpiSurgicalHistoryColumn: 'telemed-hpi-surgical-history-column',
    hpiSurgicalHistoryPatientProvidedList: 'telemed-hpi-surgical-history-patient-provided-list',
    hpiAdditionalQuestions: (questionSymptom: string) => `telemed-additional-questions-${questionSymptom}`,
    hpiAdditionalQuestionsPatientProvided: (questionSymptom: string) =>
      `telemed-additional-questions-patient-provided-${questionSymptom}`,
    hpiSurgicalHistoryNote: 'telemed-hpi-surgical-history-note',
    hpiChiefComplaintNotes: 'telemed-chief-complaint-notes',
    hpiChiefComplaintRos: 'telemed-chief-complaint-ros',
    hpiPatientConditionPhotos: 'telemed-patient-condition-photos',
    hpiReasonForVisit: 'telemed-reason-for-visit',
    videoRoomContainer: 'telemed-video-room-container',
    endVideoCallButton: 'telemed-end-video-call-button',
    pinVideoCallButton: 'telemed-pin-video-call-button',
    finishVisitButton: 'telemed-finish-visit-button',
    appointmentVisitTabs: (tab: TelemedAppointmentVisitTabs) => `telemed-appointment-visit-tab-${tab}`,
    patientInfoConfirmationCheckbox: 'telemed-patient-info-confirmation-checkbox',
    signButton: 'telemed-sign-button',
    closeChartButton: 'telemed-chart-close-icon',
    planTabDispositionContainer: 'telemed-plan-tab-disposition-container',
    planTabDispositionToggleButton: (buttonName: DispositionType) =>
      `telemed-plan-tab-disposition-toggle-button-${buttonName}`,
    planTabDispositionFollowUpDropdown: 'telemed-plan-tab-disposition-follow-up-dropdown',
    planTabDispositionNote: 'telemed-plan-tab-disposition-note',
    planTabDispositionReasonForTransferDropdown: 'telemed-plan-tab-disposition-reason-for-transfer-dropdown',
    reviewTabMedicationsContainer: 'telemed-review-tab-medications-container',
    reviewTabAdditionalQuestion: (questionSymptom: string) =>
      `telemed-review-tab-additional-question-${questionSymptom}`,
    reviewTabChiefComplaintContainer: 'telemed-review-tab-chief-complaint-container',
    reviewTabRosContainer: 'telemed-review-tab-ros-container',
    reviewTabPatientInstructionsContainer: 'telemed-review-tab-patient-instructions-container',
    reviewTabExaminationsContainer: 'telemed-review-tab-examinations-container',
    examTabTable: 'telemed-exam-tab-table',
    cancelThisVisitButton: 'telemed-cancel-this-visit-button',
    inviteParticipant: 'telemed-invite-participant-button',
    editPatientButtonSideBar: 'telemed-edit-patient-button-side-bar',
    chatModalDescription: 'telemed-chat-modal-description',
    telemedNewOrExistingPatient: 'telemed-new-or-existing-patient',
    hpiSurgicalHistoryAddNoteButton: 'telemed-hpi-surgical-history-add-note-button',
    hpiSurgicalHistoryNoteIsLoading: 'telemed-hpi-surgical-history-note-is-loading',
    closeImagePreviewButton: 'close-image-preview-button',
  },
  sideMenu: {
    completeIntakeButton: 'complete-intake-button',
    sideMenuItem: (item: string): string => `menu-item-${item}`,
  },
  inPersonModal: {
    confirmationDialogue: 'confirmation-dialogue',
  },
  hospitalizationPage: {
    hospitalizationTitle: 'hospitalization-title',
  },
  progressNotePage: {
    reviewAndSignButton: 'review-and-sign-button',
    unlockAppointmentButton: 'unlock-appointment-button',
    dischargeButton: 'discharge-button',
    sendFaxButton: 'send-fax-button',
    dischargeSummaryButton: 'discharge-summary-button',
    missingCard: 'missing-card',
    missingCardText: 'missing-card-text',
    primaryDiagnosisLink: 'primary-diagnosis-link',
    secondaryDiagnosisLink: 'secondary-diagnosis-link',
    medicalDecisionLink: 'medical-decision-link',
    emCodeLink: 'em-code-link',
    hpiLink: 'hpi-link',
    ccLink: 'chief-complaint-link',
    visitNoteCard: 'visit-note-card',
    procedureItem: 'procedure-item',
    vaccineItem: 'vaccine-item',
    inHouseMedicationItem: 'in-house-medication-item',
    labsTitle: (labType: string) => `title-${labType.toLowerCase().replace(/\s+/g, '-')}`,
    knownAllergiesContainer: 'known-allergies-container',
    medicalConditionsContainer: 'medical-conditions-container',
    surgicalHistoryContainer: 'surgical-history-container',
    additionalQuestions: 'additional-questions',
    hpiContainer: 'hpi-container',
  },
  hpiAndTemplatesPage: {
    hpiTitle: 'hpi-title',
    hpiNotes: 'hpi-notes',
  },
  assessmentCard: {
    emCodeDropdown: 'em-code-dropdown',
    medicalDecisionField: 'medical-decision-field',
    cptCodeField: 'cpt-code-field',
    medicalDecisionLoading: 'medical-decision-loading',
  },
  diagnosisContainer: {
    diagnosisDropdown: 'diagnosis-dropdown',
    allDiagnosesContainer: 'diagnosis-container-all-diagnoses',
    primaryDiagnosis: 'diagnosis-container-primary-diagnosis',
    secondaryDiagnosis: 'diagnosis-container-secondary-diagnosis',
    primaryDiagnosisDeleteButton: 'diagnosis-container-primary-diagnosis-delete-button',
    secondaryDiagnosisDeleteButton: 'diagnosis-container-secondary-diagnosis-delete-button',
    makePrimaryButton: 'diagnosis-container-make-primary-button',
  },
  billingContainer: {
    container: 'billing-container',
    deleteButton: 'billing-container-delete-button',
    deleteCptCodeButton: (code: string) => `billing-container-delete-cpt-code-button-${code}`,
    cptCodeEntry: (code: string) => `cpt-code-entry-${code}`,
  },
  patientInfoPage: {
    patientInfoVerifiedCheckbox: 'patient-info-verified-checkbox',
  },
  inHouseMedicationsPage: {
    title: 'medications-title',
    orderButton: 'order-button',
    marTable: {
      row: 'mar-table-row',
      medicationCell: 'mar-table-medication-cell',
      statusCell: 'mar-table-status-cell',
      doseCell: 'mar-table-dose-cell',
      routeCell: 'mar-table-route-cell',
      orderedByCell: ' mar-table-ordered-by-cell',
      givenByCell: ' mar-table-given-by-cell',
      reasonCell: 'mar-table-reason-cell',
      instructionsCell: 'mar-table-instructions-cell',
    },
    medicationDetailsTab: 'medication-details-tab',
    marTab: 'mar-tab',
    pencilIconButton: 'EditOutlinedIcon',
    deleteButton: 'DeleteOutlinedIcon',
    administeredButton: 'administered-button',
    partlyAdministeredButton: 'partly-administered-button',
    notAdministeredButton: 'not-administered-button',
    medicationHistoryTableMedication: 'medication-history-table-medication',
    medicationHistoryTableType: 'medication-history-table-type',
    medicationHistoryTableWhoAdded: 'medication-history-table-who-added',
    medicationHistoryTableRow: ' medication-history-table-row',
  },
  orderMedicationPage: {
    inputField: (field: string): string => `input-${field}`,
    fillOrderToSaveButton: 'fill-order-to-save-button',
    backButton: 'back-button',
    confirmationDialogue: 'confirmation-dialogue',
  },
  visitDetailsPage: {
    cancelVisitButton: 'cancel-visit-button',
    cancelationReasonDropdown: 'cancelation-reason-dropdown',
    cancelVisitDialogue: 'cancel-visit-dialogue',
  },
  patientRecordPage: {
    seeAllPatientInfoButton: 'see-all-patient-info-button',
  },
  addInsuranceDialog: {
    id: 'add-insurance-dialog',
    type: 'add-insurance-dialog-type',
    insuranceCarrier: 'add-insurance-dialog-carrier',
    planType: 'add-insurance-dialog-plan-type',
    memberId: 'add-insurance-dialog-member-id',
    policyHoldersFirstName: 'add-insurance-dialog-policy-holders-first-name',
    policyHoldersLastName: 'add-insurance-dialog-policy-holders-last-name',
    policyHoldersMiddleName: 'add-insurance-dialog-policy-holders-middle-name',
    policyHoldersDateOfBirth: 'add-insurance-dialog-policy-holders-date-of-birth',
    policyHoldersSex: 'add-insurance-dialog-policy-holders-sex',
    streetAddress: 'add-insurance-dialog-street-address',
    addressLine2: 'add-insurance-dialog-address-line2',
    city: 'add-insurance-dialog-city',
    state: 'add-insurance-dialog-state',
    zip: 'add-insurance-dialog-zip',
    relationship: 'add-insurance-dialog-relationship',
    additionalInformation: 'add-insurance-dialog-additional-information',
    addInsuranceButton: 'add-insurance-dialog-add-insurance-button',
  },
  documentProcedurePage: {
    title: 'document-procedure-title',
    consentForProcedure: 'consent-for-procedure-checkbox',
    procedureType: 'procedure-type',
    cptCodeInput: 'cpt-code-input',
    cptCode: 'cpt-code',
    diagnosis: 'diagnosis',
    diagnosisItem: 'diagnosis-item',
    diagnosisDeleteButton: 'diagnosis-delete-button',
    performedBy: 'performed-by',
    anaesthesia: 'anaesthesia',
    site: 'site',
    sideOfBody: 'side-of-body',
    technique: 'technique',
    instruments: 'instruments',
    procedureDetails: 'procedure-details',
    specimenSent: 'specimen-sent',
    complications: 'complications',
    patientResponse: 'patient-response',
    postProcedureInstructions: 'post-procedure-instructions',
    timeSpent: 'time-spent',
    documentedBy: 'documented-by',
    saveButton: 'save-button',
  },
  proceduresPage: {
    title: 'procedures-title',
    cptCode: 'cpt-code',
    diagnosis: 'diagnosis',
    procedureType: 'procedure-type',
    documentedBy: 'documented-by',
    procedureRow: 'procedure-row',
  },
  radioButton: {
    checkedIcon: 'RadioButtonCheckedIcon',
  },
  inHouseLabsPage: {
    title: 'in-house-labs-title',
    orderButton: 'order-button',
    deleteButton: 'delete-button',
  },
  orderInHouseLabPage: {
    title: 'order-in-house-labs-title',
    orderInHouseLabButton: 'order-in-house-lab-button',
    orderAndPrintLabelButton: 'order-and-print-label-button',
    testTypeField: 'test-type',
    testTypeList: 'test-type-list',
    CPTCodeField: 'cpt-code',
    diagnosis: 'diagnosis',
    additionalDx: 'additionalDx',
    notes: 'in-house-labs-notes',
  },
  collectSamplePage: {
    testName: 'test-name',
    status: 'order-status',
    source: 'source',
    collectedBy: 'collected-by',
    collectionDate: 'collection-date',
    collectionTime: 'collection-time',
    markCollectedButton: 'mark-collected-button',
  },
  performTestPage: {
    title: 'page-title',
    testName: 'test-name',
    status: 'order-status',
    testResult: (result: string) => `test-result-${result.toLowerCase().replace(/\s+/g, '-')}`,
    submitButton: 'submit-button',
  },
  finalResultPage: {
    testName: 'test-name',
    dateAndStatus: 'order-date-status',
    resultsPDF: 'results-pdf',
    diagnose: 'diagnose',
  },
  allergies: {
    allergiesPageTitle: 'allergies-title',
    knownAllergiesColumn: 'known-allergies-column',
    knownAllergiesList: 'known-allergies-list',
    knownAllergiesPatientProvidedList: 'known-allergies-patient-provided-list',
    knownAllergiesInput: 'known-allergies-input',
    knownAllergiesListItem: 'known-allergies-list-item',
  },
  medicalConditions: {
    medicalConditionsPageTitle: 'medical-conditions-title',
    medicalConditionColumn: 'medical-condition-column',
    medicalConditionsList: 'medical-condition-list',
    medicalConditionPatientProvidedList: 'medical-condition-patient-provided-list',
    medicalConditionsInput: 'medical-condition-input',
    medicalConditionListItem: 'medical-condition-list-item',
  },
  surgicalHistory: {
    surgicalHistoryTitle: 'surgical-history-title',
    surgicalHistoryOption: 'surgical-history-option',
    surgicalHistoryColumn: 'surgical-history-column',
    surgicalHistoryList: 'surgical-history-list',
    surgicalHistoryPatientProvidedList: 'surgical-history-patient-provided-list',
    surgicalHistoryInput: 'surgical-history-input',
    surgicalHistoryListItem: 'surgical-history-list-item',
  },
  orderVaccinePage: {
    title: 'order-vaccine-title',
    vaccine: 'order-vaccine-vaccine',
    dose: 'order-vaccine-dose',
    units: 'order-vaccine-units',
    route: 'order-vaccine-route',
    location: 'order-vaccine-location',
    instructions: 'order-vaccine-instructions',
    orderedBy: 'order-vaccine-ordered-by',
    orderVaccineButton: 'order-vaccine-button',
  },

  immunizationPage: {
    title: 'immunization-title',
    newOrderButton: 'new-order-button',
    marTableRow: 'mar-table-row',
    marTableVaccineCell: 'mar-table-vaccine-cell',
    marTableStatusCell: 'mar-table-status-cell',
    marTableDoseRouteCell: 'mar-table-dose-route-cell',
    marTableInstructionsCell: 'mar-table-instructions-cell',
    marTableOrderedDateCell: 'mar-table-ordered-date-cell',
    marTableOrderedPersonCell: 'mar-table-ordered-person-cell',
    marTableGivenDateCell: 'mar-table-given-date-cell',
    marTableGivenPersonCell: 'mar-table-given-person-cell',
    marTableReasonCell: 'mar-table-reason-cell',
    vaccineDetailsTab: 'vaccine-details-tab',
    marTab: 'immunization-mar-tab',
    pencilIconButton: 'EditOutlinedIcon',
    deleteButton: 'DeleteOutlinedIcon',
  },

  vaccineDetailsPage: {
    lotNumber: 'lot-number',
    expiredDate: 'expired-date',
    mvxCode: 'mvx-code',
    cvxCode: 'cvx-code',
    cptCode: 'cpt-code',
    ndcCode: 'ndc-code',
    administeredDate: 'administered-date',
    administeredTime: 'administered-time',
    visCheckbox: 'vis-checkbox',
    visGivenDate: 'vis-given-date',
    relationship: 'relationship',
    fullName: 'fullName',
    mobile: 'mobile',
    administeredButton: 'administered-button',
    partlyAdministeredButton: 'partly-administered-button',
    notAdministeredButton: 'not-administered-button',
  },

  administrationConfirmationDialog: {
    patient: 'administration-dialog-patient',
    vaccine: 'administration-dialog-vaccine',
    message: 'administration-dialog-message',
    reasonField: 'administration-dialog-reason-field',
  },

  inHouseMedicationAdministrationConfirmationDialog: {
    patient: 'administration-dialog-patient',
    medication: 'administration-dialog-medication',
    message: 'administration-dialog-message',
    reasonField: 'administration-dialog-reason-field',
  },

  screeningPage: {
    title: 'screening-title',
    asqQuestion: 'asq-question',
    screeningNoteField: 'screening-note-field',
    screeningNoteItem: 'screening-note-item',
    addNoteButton: 'add-screening-note-button',
    askPatientQuestion: 'ask-patient-question',
    vaccinationNoteField: 'vaccination-note-field',
    answerDropdown: 'answer-dropdown',
  },
};
